/**
 * Utilidades para renderizar imágenes de eventos de forma segura.
 *
 * Reglas:
 * - URL vacía / no string → `FALLBACK_URL`.
 * - URL con esquema `http://` (mixed content en HTTPS) → `FALLBACK_URL`.
 * - URL con esquema `https://` o data URI segura → se conserva.
 * - Cualquier otro esquema (`javascript:`, `file:`, `ftp:`) → `FALLBACK_URL`.
 *
 * Uso:
 *   <img src={sanearUrlImagen(evento.imagen)} onError={fallbackImagenEvento} />
 */

// Imagen institucional del Casino que se sirve como fallback estático.
export const FALLBACK_URL = "/evento-fallback.svg";
// Base pública del backend (para componer URLs relativas subidas).
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const ESQUEMAS_PERMITIDOS = /^(https:\/\/|data:image\/)/i;
// Rutas relativas emitidas por nuestro storage local o SVG estático.
const RUTAS_RELATIVAS_PERMITIDAS = /^(\/api\/uploads\/|\/evento-fallback\.svg)/i;

export const sanearUrlImagen = (url) => {
  if (!url || typeof url !== "string") return FALLBACK_URL;
  const trimmed = url.trim();
  if (!trimmed) return FALLBACK_URL;
  // Ruta relativa propia (upload local o fallback SVG) → absoluta.
  if (RUTAS_RELATIVAS_PERMITIDAS.test(trimmed)) {
    if (trimmed.startsWith("/api/uploads/") && BACKEND_URL) {
      return `${BACKEND_URL}${trimmed}`;
    }
    return trimmed;
  }
  if (!ESQUEMAS_PERMITIDOS.test(trimmed)) return FALLBACK_URL;
  return trimmed;
};

/**
 * Handler `onError` de `<img>`. Cambia src al fallback una sola vez
 * (evita loops si el propio fallback fallara).
 */
export const fallbackImagenEvento = (event) => {
  const img = event?.currentTarget;
  if (!img) return;
  if (img.dataset.fallbackApplied === "1") return;
  img.dataset.fallbackApplied = "1";
  img.src = FALLBACK_URL;
};

/**
 * Indica si una URL es segura para renderizar sin correr al fallback.
 * Útil para condicionar el `alt`, el layout o tracking.
 */
export const esUrlImagenSegura = (url) => {
  if (!url || typeof url !== "string") return false;
  const t = url.trim();
  return (
    ESQUEMAS_PERMITIDOS.test(t) || RUTAS_RELATIVAS_PERMITIDAS.test(t)
  );
};

/**
 * Fase B · Renovación controlada de la URL firmada.
 *
 * Pide al backend una URL fresca (SAS de corta duración en Azure; ruta
 * relativa en local) para la imagen principal de un evento. Se usa cuando
 * la URL vigente expiró, antes de mostrar el fallback definitivo.
 *
 * Devuelve la URL (string) o `null` si no hay imagen / no autorizado /
 * error de red. Import dinámico de `api` para evitar ciclos de módulo.
 */
export const refrescarUrlImagenEvento = async (eventoId) => {
  if (!eventoId) return null;
  try {
    const { api } = await import("@/services/api");
    const { data } = await api.get(`/eventos/${eventoId}/imagen-url`);
    return data && data.url ? data.url : null;
  } catch {
    return null;
  }
};
